import { Globe, X } from "lucide-react";
import { Button } from "@headerhawk/ui";
import { useSiteScope } from "@/lib/hooks/use-site-scope";

interface SiteScopeNoticeProps {
  /** The site a link narrowed "all sites" to (`useSiteScopeContinuity`). */
  narrowedTo: string | undefined;
  onDismiss: () => void;
}

/**
 * Says that a link narrowed the header's scope to one site (task 453): an
 * Overview row opens its own site's page, which used to switch "All sites"
 * to that site with nothing but the header's control to say so.
 */
export function SiteScopeNotice({
  narrowedTo,
  onDismiss,
}: SiteScopeNoticeProps) {
  const { siteId, site, sites, setSiteId } = useSiteScope();

  if (!narrowedTo || siteId !== narrowedTo || !site || sites.length < 2) {
    return null;
  }

  return (
    <div
      role="status"
      data-testid="site-scope-notice"
      className="flex items-center gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm"
    >
      <Globe className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <p className="min-w-0 flex-1">
        Showing <span className="font-medium">{site.name}</span> only, the site
        of the item you opened.
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setSiteId(undefined);
          onDismiss();
        }}
      >
        Show all sites
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label="Dismiss"
        onClick={onDismiss}
      >
        <X className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  );
}
